import { fromBech32, toAscii, toBech32, toUtf8 } from "@cosmjs/encoding";
import { Uint64 } from "@cosmjs/math";
import { sha256, Sha256 } from "./signers/cosmos/crypto";
import { assert } from "./signers/cosmos/utils";

/**
 * The "Basic Address" Hash from
 * https://github.com/cosmos/cosmos-sdk/blob/v0.45.8/docs/architecture/adr-028-public-key-addresses.md
 */
function hash(type: string, key: Uint8Array): Uint8Array {
  return new Sha256(sha256(toAscii(type))).update(key).digest();
}

/**
 * Takes an integer [0, 2**64-1] and returns a one-byte encoding of it.
 */
function toUint64(int: number): Uint8Array {
  return Uint64.fromNumber(int).toBytesBigEndian();
}

/**
 * Private function to export test vector data for https://github.com/cosmos/cosmjs/pull/1253.
 * Do not use in production code.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function _instantiate2AddressIntermediate(
  checksum: Uint8Array,
  creator: string,
  salt: Uint8Array,
  msg: string | null,
  prefix: string
): { key: Uint8Array; addressData: Uint8Array; address: string } {
  assert(checksum.length === 32);
  const creatorData = fromBech32(creator).data;

  const msgData = typeof msg === "string" ? toUtf8(msg) : new Uint8Array();

  // Validate inputs
  if (salt.length < 1 || salt.length > 64)
    throw new Error("Salt must be between 1 and 64 bytes");

  const key = new Uint8Array([
    ...toAscii("wasm"),
    0x00,
    ...toUint64(checksum.length),
    ...checksum,
    ...toUint64(creatorData.length),
    ...creatorData,
    ...toUint64(salt.length),
    ...salt,
    ...toUint64(msgData.length),
    ...msgData,
  ]);
  const addressData = hash("module", key);
  const address = toBech32(prefix, addressData);
  return { key, addressData, address };
}

/**
 * Predictable address generation for the MsgInstantiateContract2
 * introduced with wasmd 0.29.
 *
 * With `checksum`, `creator` and `salt`, the instantiate 2 address is
 * generated in binary form. The `bech32Prefix` is then used for the bech32 representation.
 * Chains using address formats other than bech32 are not supported by this API.
 */
export function instantiate2Address(
  checksum: Uint8Array,
  creator: string,
  salt: Uint8Array,
  bech32Prefix: string
): string {
  // Non-empty msg values are discouraged.
  // See https://medium.com/cosmwasm/dev-note-3-limitations-of-instantiate2-and-how-to-deal-with-them-a3f946874230.
  const msg = null;
  return _instantiate2AddressIntermediate(
    checksum,
    creator,
    salt,
    msg,
    bech32Prefix
  ).address;
}
